/**
 * AgencySessionContext — manages an active "operate as client" session.
 *
 * When a session is active:
 *   - sessionId and clientOrg are set
 *   - the shared Supabase client sends x-support-session-id on every request,
 *     scoping RLS to the client org for existing app data hooks
 *   - The AppShell banner is rendered by components reading this context
 */
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { createBrowserClient } from "@supabase/ssr";
import type { Database } from "@/types/database";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { getActiveAgencySession, getDefaultAgencySessionExpiresAt, setActiveAgencySession } from "./session-store";

interface AgencySessionValue {
  sessionId: string | null;
  clientOrgId: string | null;
  clientOrgName: string | null;
  clientOrgSlug: string | null;
  startSession: (sessionId: string, clientOrgId: string, clientOrgName: string, clientOrgSlug: string) => void;
  endSession: () => void;
  /** The shared Supabase client while a support-session header is active. */
  sessionClient: ReturnType<typeof createBrowserClient<Database, "relay">> | null;
}

const AgencySessionContext = createContext<AgencySessionValue>({
  sessionId: null,
  clientOrgId: null,
  clientOrgName: null,
  clientOrgSlug: null,
  startSession: () => {},
  endSession: () => {},
  sessionClient: null,
});

export function AgencySessionProvider({ children }: { children: ReactNode }) {
  // Rehydrate from sessionStorage so an active operate-as session survives a
  // full page reload instead of silently reverting to the user's base org.
  const persisted = getActiveAgencySession();
  const [sessionId, setSessionId] = useState<string | null>(persisted?.sessionId ?? null);
  const [clientOrgId, setClientOrgId] = useState<string | null>(persisted?.clientOrgId ?? null);
  const [clientOrgName, setClientOrgName] = useState<string | null>(persisted?.clientOrgName ?? null);
  const [clientOrgSlug, setClientOrgSlug] = useState<string | null>(persisted?.clientOrgSlug ?? null);
  const [sessionClient, setSessionClient] = useState<ReturnType<typeof createBrowserClient<Database, "relay">> | null>(
    persisted ? supabase : null,
  );
  const queryClient = useQueryClient();
  const { user, isLoading: authLoading } = useAuth();
  const authUserId = user?.id ?? null;

  const resetAppDataCache = useCallback(() => {
    queryClient.clear();
  }, [queryClient]);

  const startSession = useCallback((id: string, orgId: string, orgName: string, orgSlug: string) => {
    if (!authUserId) return;
    setActiveAgencySession({
      authUserId,
      sessionId: id,
      clientOrgId: orgId,
      clientOrgName: orgName,
      clientOrgSlug: orgSlug,
      expiresAt: getDefaultAgencySessionExpiresAt(),
    });
    setSessionId(id);
    setClientOrgId(orgId);
    setClientOrgName(orgName);
    setClientOrgSlug(orgSlug);
    setSessionClient(supabase);
    resetAppDataCache();
  }, [authUserId, resetAppDataCache]);

  const endSession = useCallback(() => {
    setActiveAgencySession(null);
    setSessionId(null);
    setClientOrgId(null);
    setClientOrgName(null);
    setClientOrgSlug(null);
    setSessionClient(null);
    resetAppDataCache();
  }, [resetAppDataCache]);

  // Discard a rehydrated session that belongs to a different user (or once the
  // user signs out) — but NOT on same-user token refresh / focus re-fires,
  // which would wrongly drop an active operate-as session. Runs only after auth
  // hydration settles so we don't clear during the initial loading window.
  useEffect(() => {
    if (authLoading) return;

    if (!authUserId) {
      if (sessionId) endSession();
      return;
    }

    const stored = getActiveAgencySession();
    if (sessionId && (!stored || stored.authUserId !== authUserId)) {
      endSession();
    }
  }, [authLoading, authUserId, endSession, sessionId]);

  return (
    <AgencySessionContext.Provider value={{ sessionId, clientOrgId, clientOrgName, clientOrgSlug, startSession, endSession, sessionClient }}>
      {children}
    </AgencySessionContext.Provider>
  );
}

export function useAgencySession() {
  return useContext(AgencySessionContext);
}
